import { useAccessPolicy } from '@/components/people/people/hooks';
import { type User } from '@/shared/types/user';
import { type UserAccess, useUserAccess, useUserId } from '@/hooks/auth';
import {
  getMinistryStructure,
  getOrgAlias,
  OrgProperties,
  type OrgTypeKey,
  type MinistryRoleKey,
} from '../org/types';
import { useCampuses, useMinistries } from '../org/hooks';
import { useGroups } from '../people/hooks';
import { useMemo } from 'react';
import type { Person } from '../people/types';
import type {
  Effect,
  PeopleAccess,
  PeopleAccessAction,
  PeopleRoleKey,
} from '@/shared/types/policy';
import type { Ministry, MinistryTypeKey } from '@/shared/types/ministry';
import type { Campus, CampusRoleKey } from '@/shared/types/campus';
import type { Group, GroupTypeKey } from '@/shared/types/group';

export const PeopleAccessEntries: Record<PeopleAccessAction, string> = {
  people_view: 'View People',
  people_create: 'Create People',
  people_edit: 'Edit People',
  people_archive: 'Archive People',
  comment_view: 'View Comments',
  comment_create: 'Create Comments',
  comment_edit: 'Edit Comments',
  comment_archive: 'Archive Comments',
  comment_sensitive_view: 'View Sensitive Comments',
  comment_sensitive_flag: 'Flag Sensitive Comments',
  structure_view: 'View Structure',
  structure_create: 'Create Structure',
  structure_edit: 'Edit Structure',
  structure_archive: 'Archive Structure',
  access_view: 'View Access',
  access_edit: 'Edit Access',
};

export interface Role {
  key: string;
  label: string;
  description: string;
  builtIn?: boolean;
}

type RoleMap = {
  s: Scope;
  o?: OrgTypeKey;
  m?: MinistryTypeKey;
  g?: GroupTypeKey;
  r: string;
};

export const PeopleRoles: Record<PeopleRoleKey, Role> = {
  system_admins: {
    key: 'system_admins',
    label: 'System Admins x',
    description: 'Users with full system admin access',
  },
  all_people_admins: {
    key: 'all_people_admins',
    label: 'All People Admins',
    description: 'Users with with full access to manage people',
  },
  campus_people_admins: {
    key: 'campus_people_admins',
    label: 'Campus People Admins',
    description:
      'Administrators with access to manage people at the campus level',
  },
  lead_pastors: {
    key: 'lead_pastors',
    label: 'Lead Pastors',
    description: 'Lead pastors with oversight responsibilities',
  },
  all_campus_pastors: {
    key: 'all_campus_pastors',
    label: 'All Campus Pastors',
    description: 'All pastors with campus responsibilities',
  },
  assigned_campus_pastors: {
    key: 'assigned_campus_pastors',
    label: 'Assigned Campus Pastors',
    description: 'Pastors with specific campus responsibilities',
  },
  all_lay_pastors: {
    key: 'all_lay_pastors',
    label: 'All Lay Pastors',
    description: 'All lay pastors',
  },
  campus_lay_pastors: {
    key: 'campus_lay_pastors',
    label: 'Campus Lay Pastors',
    description: 'Lay pastors with specific responsibilities',
  },
  assigned_lay_pastors: {
    key: 'assigned_lay_pastors',
    label: 'Assigned Lay Pastors',
    description: 'Lay pastors with specific responsibilities',
  },
  all_lay_leaders: {
    key: 'all_lay_leaders',
    label: 'All Lay Leaders',
    description: 'All lay leaders',
  },
  campus_lay_leaders: {
    key: 'campus_lay_leaders',
    label: 'Campus Lay Leaders',
    description: 'All campus lay leaders',
  },
  section_lay_leaders: {
    key: 'section_lay_leaders',
    label: 'Section Lay Leaders',
    description: 'Leaders of specific laygroups',
  },
  assigned_lay_leaders: {
    key: 'assigned_lay_leaders',
    label: 'Assigned Lay Leaders',
    description: 'Leaders of specific lay groups',
  },
  all_ministry_pastors: {
    key: 'all_ministry_pastors',
    label: 'All Ministry Pastors',
    description: 'All pastors with ministry responsibilities',
  },
  campus_ministry_pastors: {
    key: 'campus_ministry_pastors',
    label: 'Campus Ministry Pastors',
    description: 'Pastors with specific ministry responsibilities',
  },
};

export function peopleRolesList1() {
  return Object.values(PeopleRoles);
}

export type Scope = 'all' | 'campus' | 'adjacent' | 'assigned';

export function getRoleName(scope: Scope, org: OrgTypeKey, role: string) {
  return `s=${scope}/o=${org}/r=${role}`;
}

export function parseRoleName(role: string) {
  const res: RoleMap = {
    s: '' as Scope,
    o: undefined as OrgTypeKey | undefined,
    m: undefined as MinistryTypeKey | undefined,
    g: undefined as GroupTypeKey | undefined,
    r: '',
  };
  const parts = role.split('/');
  for (const part of parts) {
    const values = part.split('=');
    switch (values[0]) {
      case 's':
        res.s = values[1] as Scope;
        break;
      case 'o':
        res.o = values[1] as OrgTypeKey;
        break;
      case 'm':
        res.m = values[1] as MinistryTypeKey;
        break;
      case 'g':
        res.g = values[1] as GroupTypeKey;
        break;
      case 'r':
        res.r = values[1] as string;
        break;
      default:
        console.error(`role key ${values[0]} not recognized`);
    }
  }

  return res;
}

export function getGroupRoleName(
  scope: Scope,
  ministry: MinistryTypeKey,
  type: GroupTypeKey,
  role: string,
) {
  return `s=${scope}/m=${ministry}/g=${type}/r=${role}`;
}

export function peopleRolesList(): Role[] {
  const roles: Role[] = [
    {
      key: getRoleName('all', 'organization', 'admins'),
      description: '',
      label: 'System Admins',
    },
  ];
  const campus = OrgProperties.campus;
  for (const role of Object.values(campus.roles)) {
    roles.push({
      key: getRoleName('all', 'campus', role.key),
      description: '',
      label: 'All ' /*+ campus.alias.type + ' '*/ + role.plural,
    });
    roles.push({
      key: getRoleName('assigned', 'campus', role.key),
      description: '',
      label: 'Assigned ' /*+ campus.alias.type + ' '*/ + role.plural,
    });
  }

  const ministry = OrgProperties.ministry;
  for (const role of Object.values(ministry.roles)) {
    roles.push({
      key: getRoleName('all', 'ministry', role.key),
      description: '',
      label: 'All ' /*+ ministry.alias.type + ' '*/ + role.plural,
    });
    roles.push({
      key: getRoleName('campus', 'ministry', role.key),
      description: '',
      label: campus.alias.type + ' ' + role.plural,
    });
    roles.push({
      key: getRoleName('assigned', 'ministry', role.key),
      description: '',
      label: 'Assigned ' /*+ ministry.alias.type + ' '*/ + role.plural,
    });
  }

  // {
  //   key: 'all_campus_pastors',
  //   description: '',
  //   label: 'All ' + getOrgAlias('campus', 'oversight', true),
  // },
  // {
  //   key: 'assigned_campus_pastors',
  //   description: '',
  //   label: 'Assigned ' + getOrgAlias('campus', 'oversight', true),
  // },
  // {
  //   key: 'all_ministry_pastors',
  //   description: '',
  //   label: 'All ' + getOrgAlias('ministry', 'oversight', true),
  // },
  // {
  //   key: 'campus_ministry_pastors',
  //   description: '',
  //   label:
  //     getOrgAlias('campus', 'type') +
  //     ' ' +
  //     getOrgAlias('ministry', 'oversight', true),
  // },
  // {
  //   key: 'assigned_ministry_pastors',
  //   description: '',
  //   label: 'Assigned ' + getOrgAlias('ministry', 'oversight', true),
  // },
  // ];
  for (const group of getMinistryStructure('pastoral_care') || []) {
    for (const role of group.roles) {
      roles.push({
        key: getGroupRoleName('all', group.ministry, group.type, role.key),
        description: '',
        label: 'All ' + group.label + ' ' + role.plural,
      });
      roles.push({
        key: getGroupRoleName('campus', group.ministry, group.type, role.key),
        description: '',
        label:
          getOrgAlias('campus', 'type') + ' ' + group.label + ' ' + role.plural,
      });
      roles.push({
        key: getGroupRoleName('assigned', group.ministry, group.type, role.key),
        description: '',
        label: 'Assigned ' + group.label + ' ' + role.plural,
      });
      if (group.parent) {
        roles.push({
          key: getGroupRoleName(
            'adjacent',
            group.ministry,
            group.type,
            role.key,
          ),
          description: '',
          label: 'Adjacent ' + group.label + ' ' + role.plural,
        });
      }
    }
  }

  return roles;
}

// Policy Rules set from the top down - can be augmented at lower levels - Deny at any level means no access
// Policy is built dynamically based on rules and context - evaluated for access allow or deny
// Effect: Allow | Deny
// Campus oversight view TODO - new Organization view - Entities (nice to have - for Lead Pastor access especially) and Campuses
// Principals : #todo specific user, all Users, context specific users (e.g laygroup oversights, campus pastors)
// People Access tab - rules for access policy at each level in the structure

export function roleMembersAdmins(users: User[]) {
  return new Set(
    users.filter((user) => user.data.admin).map((user) => user.id),
  );
}

// TODO: roleMembersPeopleAdmins

export function roleMembersAllCampus(campuses: Campus[], role: CampusRoleKey) {
  const members = new Set(
    campuses.map((item) => item.data.roles?.[role] || []).flat(),
  );

  return members;
}

export function roleMembersAssignedCampus(
  lookup: Record<string, Campus>,
  role: CampusRoleKey,
  campus_id?: string,
) {
  if (!campus_id) {
    return null;
  }

  const item = lookup[campus_id];

  return new Set(item?.data.roles?.[role] || []);
}

export function roleMembersAllMinistry(
  ministries: Ministry[],
  role: MinistryRoleKey,
) {
  return new Set(
    ministries.map((item) => item.data.roles?.[role] || []).flat(),
  );
}

export function roleMembersCampusMinistry(
  ministries: Record<string, Record<MinistryTypeKey, Ministry>>,
  role: MinistryRoleKey,
  campus_id?: string,
) {
  if (!campus_id) {
    return null;
  }

  const entries = ministries[campus_id] || [];
  const members = new Set(
    Object.values(entries)
      .map((item) => item.data.roles?.[role] || [])
      .flat(),
  );

  return members;
}

export function roleMembersAssignedMinistry(
  ministries: Record<string, Record<MinistryTypeKey, Ministry>>,
  role: MinistryRoleKey,
  campus_id?: string,
  ministry?: MinistryTypeKey,
) {
  if (!campus_id || !ministry) {
    return null;
  }

  const entries = ministries[campus_id];
  const entry = entries[ministry];
  const members = new Set(entry?.data.roles?.[role] || []);

  return members;
}

export function roleMembersCampusLayPastors(
  laygroups: Group[],
  campus_id?: string,
) {
  if (!campus_id) {
    return null;
  }

  const oversight_ids = new Set(
    laygroups
      .filter(
        (item) =>
          item.data.campus_id === campus_id && item.data.type === 'laygroup1',
      )
      .map((item) => item.data.oversight_ids)
      .flat(),
  );

  return oversight_ids;
}

export function roleMembersAllLayPastors(laygroups: Group[]) {
  const oversight_ids = new Set(
    laygroups
      .filter((item) => item.data.type === 'laygroup1')
      .map((item) => item.data.oversight_ids)
      .flat(),
  );

  return oversight_ids;
}

export function roleMembersAssignedLayPastors(
  laygroups: Record<string, Group>,
  id?: string,
) {
  if (!id) {
    return null;
  }

  const oversight_ids = new Set(laygroups[id]?.data.oversight_ids || []);

  return oversight_ids;
}

export function roleMembersCampusGroupLeaders(
  groups: Group[],
  type: GroupTypeKey,
  campus_id?: string,
) {
  if (!campus_id) {
    return null;
  }

  const oversight_ids = new Set(
    groups
      .filter(
        (item) => item.data.campus_id === campus_id && item.data.type === type,
      )
      .map((item) => item.data.oversight_ids)
      .flat(),
  );

  return oversight_ids;
}

export function roleMembersAllGroupLeaders(
  groups: Group[],
  type: GroupTypeKey,
) {
  const oversight_ids = new Set(
    groups
      .filter((item) => item.data.type === type)
      .map((item) => item.data.oversight_ids)
      .flat(),
  );

  return oversight_ids;
}

export function roleMembersAdjacentGroupLeaders(
  groups: Group[],
  type: GroupTypeKey,
  parent_id?: string,
) {
  if (!parent_id) {
    return null;
  }

  const oversight_ids = new Set(
    groups
      .filter(
        (item) => item.data.type === type && item.data.parent_id === parent_id,
      )
      .map((item) => item.data.oversight_ids)
      .flat(),
  );

  return oversight_ids;
}

export function roleMembersAssignedGroupLeaders(
  groups: Record<string, Group>,
  id?: string,
) {
  if (!id) {
    return null;
  }

  const oversight_ids = new Set(groups[id]?.data.oversight_ids || []);

  return oversight_ids;
}

export interface PeopleContext {
  campus_id?: string;
  ministry?: MinistryTypeKey;
  group1?: string;
  group2?: string;
}

function computeAccess(
  userId: string,
  context: PeopleContext,
  userAccess: UserAccess | null,
  policy: ReturnType<typeof useAccessPolicy>,
  groupList: Group[],
  groupLookup: Record<string, Group>,
  campusList: Campus[],
  campusLookup: Record<string, Campus>,
  ministryList: Ministry[],
  ministryLookup: Record<string, Record<MinistryTypeKey, Ministry>>,
) {
  const defaultEffect: Effect = userAccess?.admin ? 'allow' : '';
  const access: PeopleAccess = {
    people_view: defaultEffect,
    people_create: defaultEffect,
    people_edit: defaultEffect,
    people_archive: defaultEffect,
    comment_view: defaultEffect,
    comment_create: defaultEffect,
    comment_edit: defaultEffect,
    comment_archive: defaultEffect,
    comment_sensitive_view: defaultEffect,
    comment_sensitive_flag: defaultEffect,
    structure_view: defaultEffect,
    structure_create: defaultEffect,
    structure_edit: defaultEffect,
    structure_archive: defaultEffect,
    access_view: defaultEffect,
    access_edit: defaultEffect,
  };
  const roles: PeopleRoleKey[] = [];
  // const roleEntries = Object.entries(policy);
  const roleEntries = peopleRolesList();
  // for (const [roleKey, roleAccess] of roleEntries) {
  for (const entry of roleEntries) {
    // const role = roleKey as PeopleRoleKey;
    const role = entry.key;
    const roleAccess = policy.policy[role as PeopleRoleKey];
    if (!roleAccess) {
      continue;
    }

    // if (roleMembersAdmins(role as PeopleRoleKey).has(userId)) {
    //   for (const [action, effect] of Object.entries(roleAccess)) {
    //     access[action as PeopleAccessAction] = effect;
    //   }
    // }
    const applyRoleAccess = (
      roleSet: Set<string> | null | undefined,
      role: PeopleRoleKey,
    ) => {
      if (roleSet?.has(userId)) {
        for (const action in roleAccess) {
          const effect = roleAccess[action] as Effect;
          if (!roles.includes(role)) {
            roles.push(role);
          }

          if (effect === 'deny' || (effect && access[action] !== 'deny')) {
            access[action as PeopleAccessAction] = effect;
          }
        }
      }
    };

    const rn = parseRoleName(role);

    let members: Set<string> | null = null;

    if (rn.o === 'campus') {
      if (rn.s === 'all') {
        members = roleMembersAllCampus(campusList, rn.r as CampusRoleKey);
      } else if (rn.s === 'assigned') {
        members = roleMembersAssignedCampus(
          campusLookup,
          rn.r as CampusRoleKey,
          context.campus_id,
        );
      }
    } else if (rn.o === 'ministry') {
      if (rn.s === 'all') {
        members = roleMembersAllMinistry(ministryList, rn.r as MinistryRoleKey);
      } else if (rn.s === 'campus') {
        members = roleMembersCampusMinistry(
          ministryLookup,
          rn.r as MinistryRoleKey,
          context.campus_id,
        );
      } else if (rn.s === 'assigned') {
        members = roleMembersAssignedMinistry(
          ministryLookup,
          rn.r as MinistryRoleKey,
          context.campus_id,
          context.ministry,
        );
      }
    } else if (rn.m === 'pastoral_care') {
      if (rn.s === 'all') {
        members = roleMembersAllGroupLeaders(groupList, rn.g as GroupTypeKey);
      } else if (rn.s === 'campus') {
        members = roleMembersCampusGroupLeaders(
          groupList,
          rn.g as GroupTypeKey,
          context.campus_id,
        );
      } else if (rn.s === 'assigned') {
        members = roleMembersAssignedGroupLeaders(
          groupLookup,
          rn.g === 'laygroup1' ? context.group1 : context.group2,
        );
      } else if (rn.s === 'adjacent') {
        members = roleMembersAdjacentGroupLeaders(
          groupList,
          rn.g as GroupTypeKey,
          context.group1,
        );
      }
    }

    if (members?.size) {
      applyRoleAccess(members, role as PeopleRoleKey);
    }

    // switch (role) {
    //   case 'all_campus_pastors':
    //     applyRoleAccess(roleMembersAllCampusPastors(campusList), role);
    //     break;
    //   case 'assigned_campus_pastors':
    //     applyRoleAccess(
    //       roleMembersAssignedCampusPastors(campusLookup, context.campus_id),
    //       role,
    //     );
    //     break;
    //   case 'campus_lay_pastors':
    //     applyRoleAccess(
    //       roleMembersCampusLayPastors(groupList, context.campus_id),
    //       role,
    //     );
    //     break;
    //   case 'all_lay_pastors':
    //     applyRoleAccess(roleMembersAllLayPastors(groupList), role);
    //     break;
    //   case 'assigned_lay_pastors':
    //     applyRoleAccess(
    //       roleMembersAssignedLayPastors(groupLookup, context.group1),
    //       role,
    //     );
    //     break;
    //   case 'campus_lay_leaders':
    //     applyRoleAccess(
    //       roleMembersCampusLayLeaders(groupList, context.campus_id),
    //       role,
    //     );
    //     break;
    //   case 'all_lay_leaders':
    //     applyRoleAccess(roleMembersAllLayLeaders(groupList), role);
    //     break;
    //   case 'section_lay_leaders':
    //     applyRoleAccess(
    //       roleMembersSectionLayLeaders(groupList, context.group1),
    //       role,
    //     );
    //     break;
    //   case 'assigned_lay_leaders':
    //     applyRoleAccess(
    //       roleMembersAssignedLayLeaders(groupLookup, context.group2),
    //       role,
    //     );
    //     break;
    //   case 'all_ministry_pastors':
    //     applyRoleAccess(roleMembersAllMinistryPastors(ministryList), role);
    //     break;
    //   case 'campus_ministry_pastors':
    //     applyRoleAccess(
    //       roleMembersCampusMinistryPastors(ministryLookup, context.campus_id),
    //       role,
    //     );
    // }
  }

  return { access, roles };
}

export function useUserPeopleAccess(
  userId: string,
  context: PeopleContext,
  // policy: Record<PeopleRoleKey, PeopleAccess>,
) {
  const userAccess = useUserAccess();
  const policy = useAccessPolicy();
  const { list: groupList, lookup: groupLookup } = useGroups(context.campus_id);
  const { list: campusList, lookup: campusLookup } = useCampuses();
  const { list: ministryList, lookup: ministryLookup } = useMinistries();

  return useMemo(
    () =>
      computeAccess(
        userId,
        context,
        userAccess,
        policy,
        groupList,
        groupLookup,
        campusList,
        campusLookup,
        ministryList,
        ministryLookup,
      ),
    [
      userId,
      context,
      userAccess,
      policy,
      groupList,
      groupLookup,
      campusList,
      campusLookup,
      ministryList,
      ministryLookup,
    ],
  );
}

export function useMyPeopleAccess(context: PeopleContext) {
  const userId = useUserId();
  const { access, roles } = useUserPeopleAccess(userId || '', context);

  return useMemo(() => ({ access, roles }), [access, roles]);
}

export function useSpecificPeopleAccess(person: Person | null) {
  const context: PeopleContext = {
    campus_id: person?.data.campus_id,
    ministry: 'pastoral_care',
    group1: person?.data.groups?.laygroup1,
    group2: person?.data.groups?.laygroup2,
  };

  return useMyPeopleAccess(context);
}

export function computeInferredContext(
  userId: string | undefined,
  campuses: Campus[],
  groups: Group[],
) {
  if (!userId) {
    return null;
  }

  const roles = peopleRolesList();
  const campusIds = new Set<string>();
  const groupIds = new Set<string>();
  for (const role of roles) {
    const rn = parseRoleName(role.key);
    if (rn.o === 'campus') {
      for (const campus of campuses) {
        const members: string[] =
          campus.data.roles?.[rn.r as CampusRoleKey] || [];
        if (members.includes(userId)) {
          campusIds.add(campus.id);
        }
      }
    } else if (rn.g === 'laygroup1' || rn.g === 'laygroup2') {
      for (const group of groups) {
        const members = group.data.oversight_ids || [];
        if (members.includes(userId)) {
          groupIds.add(group.id);
        }
      }
    }
  }

  const res = {
    campusIds,
    groupIds,
    excluded: !campusIds.size && !groupIds.size,
  };

  return res;
}

export function useInferredContext() {
  const userId = useUserId();
  const { list: campuses } = useCampuses();
  const { list: groups } = useGroups();

  return useMemo(() => {
    return computeInferredContext(userId, campuses, groups);
  }, [campuses, groups, userId]);
}
